import { Component, OnDestroy, OnInit, NgZone, ViewChild } from '@angular/core';

import { IonRouterOutlet, Platform } from '@ionic/angular';
import { StatusBar, Style } from '@capacitor/status-bar';
import { AuthenticationService } from './core/services/authentication.service';
import { Router, RouterEvent } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { PushService } from './core/services/push.service';
import { environment } from '@env';
import { NavigationService } from './core/services/navigation.service';
import { App, AppState, BackButtonListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { SituationService } from './core/services/situation.service';
import { SituationRecordDTO } from 'ticket-sdk';
import { TermsModalService } from './core/services/terms-modal.service';
import { UrlExtendedService } from './core/services/url-extended.service';


declare let cordova: any;

interface AppPage {
  title: string,
  url: string,
  icon: string,
  secure: boolean,
  external: boolean,
  relatedUrls: string[]
}

declare global {
  interface Window {
      _klarnaCheckout:any;
      scoApi: any;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  appVersion = environment.appVersion;
  isAuthenticated: boolean;

  public selectedIndex = 0;
  public appPages: AppPage[] = [
    {
      title: 'Köp biljett',
      url: '/buy-ticket',
      icon: 'receipt',
      secure: false,
      external: false,
      relatedUrls: ['/checkout']
    },
    {
      title: 'Mina biljetter',
      url: '/tickets',
      icon: 'pricetags',
      secure: true,
      external: false,
      relatedUrls: []
    },
    {
      title: 'Köphistorik',
      url: '/order-history',
      icon: 'file-tray-full',
      secure: false,
      external: false,
      relatedUrls: []
    },
    {
      title: 'Tidtabell',
      url: '/timetable',
      icon: 'time',
      secure: false,
      external: false,
      relatedUrls: []
    },
    {
      title: 'Trafikinformation',
      url: '/situations',
      icon: 'notifications',
      secure: false,
      external: false,
      relatedUrls: []
    },
    {
      title: 'Frågor & svar',
      url: '', // Fetch from urlExtendedService
      icon: 'help',
      secure: false,
      external: true,
      relatedUrls: []
    },
    {
      title: 'Mitt konto',
      url: '/my-account',
      icon: 'person',
      secure: true,
      external: false,
      relatedUrls: []
    },
  ];
  
  private onDestroy = new Subject();

  constructor(
    private platform: Platform,
    private authenticationService: AuthenticationService,
    private pushService: PushService,
    private router: Router,
    private zone: NgZone,
    private navigationService: NavigationService,
    private termsModalService: TermsModalService,
    private situationService: SituationService,
    private urlExtendedService: UrlExtendedService
  ) {
    this.initializeApp();
  }

  get currentUrl() {
    return this.router.url;
  }

  get isIpad() {
    return this.platform.is('ipad');
  }

  get situations$(): Observable<SituationRecordDTO[]> {
    return this.situationService.getSituations();
  }

  get user() {
    return this.authenticationService.getUser();
  }

  get userFullName() {
    return `${this.user?.firstname} ${this.user?.lastname}`;
  }

  @ViewChild(IonRouterOutlet, { static : true }) routerOutlet: IonRouterOutlet;

  initializeApp() {
    this.platform.ready().then(() => {
      
      if (this.platform.is('capacitor')) {
        StatusBar.setStyle({style: Style.Default})
        this.pushService.initiate();
      }

      this.authenticationService.authenticationState.pipe(takeUntil(this.onDestroy)).subscribe(state => {
        this.isAuthenticated = state;

        if (this.isAuthenticated && this.platform.is('capacitor')) {
          this.pushService.addUserToken();
        }
      });
    });

    if (App) {
      App.addListener('appUrlOpen', (data: any) => {
        this.zone.run(() => {
          const slug = data.url.split('.se').pop();
          
          if (data.url.startsWith('ticketapp://checkout')) {
            //Do nothing, because otherwise the Klarna flow will be interrupted when user clicks the 'Done' button from external browser after Klarna purchase.
            //This is old behavior left in to support older app versions. Could be removed after some time.
          } else if (slug && slug != 'ticketapp://') {
            this.router.navigateByUrl(slug);
          } 
        })
      });

      App.addListener('appStateChange', (state: AppState) => {
        if (state.isActive) {
          this.situationService.updateSituations();

          if (this.platform.is('capacitor')) {
            this.pushService.clearNotifications();
          }
        }
      });

      this.registerAndHandleBackButtonEvents();
    }
  }

  async ngOnInit() {
    const path = window.location.pathname.split('folder/')[1];
    if (path !== undefined) {
      this.selectedIndex = this.appPages.findIndex(page => page.title.toLowerCase() === path.toLowerCase());
    }
    
    this.router.events.pipe(
      takeUntil(this.onDestroy),
      filter((e: any): e is RouterEvent => e instanceof RouterEvent)
    ).subscribe((e: RouterEvent) => {
      this.navigationService.setCanGoBack(this.routerOutlet.canGoBack());
    });

    // routeInfoUrl is fetched from backend
    this.urlExtendedService.$routeInfoUrl.pipe(takeUntil(this.onDestroy))
    .subscribe(routeInfoUrl => {
      let routeInfoAppPage = this.appPages.find(appPage => appPage.title === 'Frågor & svar');

      if (!!routeInfoAppPage) {
        routeInfoAppPage.url = routeInfoUrl;  
      }
    });

    this.termsModalService.activateTermsModal();
    this.situationService.updateSituations();

    //Disable swipe to go back since we have a side menu
    this.routerOutlet.swipeGesture = false;
  }

  async ngAfterViewInit() {
    if (Capacitor.getPlatform() === 'android') {
      setTimeout(async () => {
        await SplashScreen.hide();
      }, 1500);
    }
    else if (Capacitor.getPlatform() === 'ios') {
      setTimeout(async () => {
        await SplashScreen.hide();
      }, 500);
    }
  }


  ngOnDestroy() {
    this.onDestroy.next(null);
  }

  isPageSelected(page: AppPage): boolean {
      return page.url === this.currentUrl || page.relatedUrls.includes(this.currentUrl);
  }

  openExternalPage(url: string) {
    if (this.platform.is('capacitor')) {
      let browserOptions: string = '';
      browserOptions += 'closebuttoncaption=Stäng,';
      browserOptions += 'toolbarposition=top,';
      browserOptions += 'toolbartranslucent=no,';
      browserOptions += 'hidenavigationbuttons=yes,';
      browserOptions += 'toolbarcolor=#6eaed1,';
      browserOptions += 'closebuttoncolor=#ffc409,';

      if (this.platform.is('ios')) {
        browserOptions += 'location=no,';
      }
      else {
        browserOptions += 'location=yes,';
      }

      var ref = cordova.InAppBrowser.open(url, '_blank', browserOptions);
    }
    else {
      window.open(url);
    }
  }

  registerAndHandleBackButtonEvents() {
    App.addListener('backButton', (event: BackButtonListenerEvent) => {
      if (! this.routerOutlet.canGoBack()) {
        App.exitApp();
      }
    });
  }
}
